import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AuthApiService, AuthResponse} from '@npck/af-frontend-api';
import {catchError, finalize, Observable, shareReplay, switchMap, tap, throwError} from 'rxjs';
import {AuthHolderService} from '../domain/auth-holder.service';
import {LogoutService} from '../domain/logout-service';


@Injectable({
    providedIn: 'root',
})
export class RefreshTokenInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshTokenRequest?: Observable<any> = null;

    constructor(
        private authHolderService: AuthHolderService,
        private logoutService: LogoutService,
        private authApiService: AuthApiService,
    ) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status !== HttpStatusCode.Unauthorized || !this.authHolderService.isAuthorized.value) {
                    return throwError(() => error);
                }

                if (this.authHolderService.isRefreshTokenExpired()) {
                    this.resetState();
                    if (this.authHolderService.isAuthorized.value) {
                        this.logoutService.logout('session_ended');
                    }
                    return throwError(() => error);
                }

                if (this.isRefreshing) {
                    return this.refreshTokenRequest
                        .pipe(switchMap(() => next.handle(this.addAuthHeader(request))));
                }

                this.isRefreshing = true;
                this.refreshTokenRequest = this.refreshToken().pipe(
                    shareReplay({bufferSize: 1, refCount: true}),
                );

                return this.refreshTokenRequest.pipe(
                    switchMap(() => next.handle(this.addAuthHeader(request))),
                    catchError((refreshError) => {
                        this.resetState();
                        this.authHolderService.logout('session_ended');
                        return throwError(() => refreshError);
                    }),
                );
            }),
        );
    }

    private refreshToken(): Observable<AuthResponse> {
        return this.authApiService.refreshToken(
            this.authHolderService.authResponse.refresh_token,
            'default',
            'refresh_token',
        ).pipe(
            tap(newToken => this.authHolderService.storeAuthToken(newToken)),
            finalize(() => this.resetState()),
        );
    }

    private resetState(): void {
        this.isRefreshing = false;
        this.refreshTokenRequest = null;
    }

    private addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
        const authToken = this.authHolderService.authResponse.access_token;
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${authToken}`,
            },
        });
    }
}
